import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import hrApi from "./hrApi";
import SignaturePad from "./SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NameInputs } from "./NameInputs";
import { toast } from "sonner";
import { ShieldCheck, PenLine, FileText } from "lucide-react";

function Shell({ children }) {
  return <div className="min-h-screen bg-slate-100 py-8 px-4"><div className="max-w-lg mx-auto bg-white rounded-3xl shadow-sm p-6">{children}</div></div>;
}

export default function HrDo() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  const [mode, setMode] = useState("draw");
  const [sig, setSig] = useState(null);
  const [name, setName] = useState("");
  const [ack, setAck] = useState(false);
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    hrApi.get(`/public/template/${token}`).then((r) => { setInfo(r.data); if (r.data.completed) setDone(true); })
      .catch((e) => setErr(e.response?.data?.detail || "This link is not valid."));
  }, [token]);

  const setV = (k, v) => setVals((s) => ({ ...s, [k]: v }));
  const submit = async () => {
    if (!name.trim()) return toast.error("Please type your first and last name");
    if (!ack) return toast.error("Please tick the acknowledgement");
    if ((mode === "draw" || mode === "upload") && !sig) return toast.error("Please add your signature");
    for (const f of info.form_fields || []) { if (f.required && !vals[f.key] && f.type !== "checkbox") return toast.error(`Please complete: ${f.label}`); if (f.required && f.type === "checkbox" && !vals[f.key]) return toast.error(`Please acknowledge: ${f.label}`); }
    setBusy(true);
    try {
      await hrApi.post(`/public/template/${token}/submit`, { typed_name: name.trim(), acknowledged: ack, signature_type: mode, signature_data: mode === "typed" ? null : sig, field_values: vals });
      setDone(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not submit"); }
    setBusy(false);
  };

  if (err) return <Shell><div className="text-center text-slate-500" data-testid="do-invalid">{err}</div></Shell>;
  if (!info) return <Shell><div className="text-center text-slate-400">Loading…</div></Shell>;
  if (done) return <Shell><div className="text-center space-y-3" data-testid="do-done">
    <div className="w-14 h-14 rounded-full bg-teal-100 grid place-items-center mx-auto"><ShieldCheck className="text-teal-600" size={28} /></div>
    <h1 className="font-heading font-extrabold text-2xl text-slate-800">{info.kind === "contract" ? "Contract signed" : "Induction completed"}</h1>
    <p className="text-slate-500">Thank you{info.employee_name ? `, ${info.employee_name.split(" ")[0]}` : ""}. A copy has been filed with G &amp; L One.</p>
  </div></Shell>;

  return (
    <Shell>
      <div data-testid="do-page" className="space-y-4">
        <div><h1 className="font-heading font-extrabold text-2xl text-slate-800">{info.name}</h1>
          <p className="text-slate-500 text-sm">Please review and {info.kind === "contract" ? "sign" : "complete"} below, {info.employee_name}.</p></div>
        {info.body && <div className="rounded-xl border border-slate-200 p-4 bg-slate-50 text-sm text-slate-700 whitespace-pre-wrap max-h-72 overflow-y-auto" data-testid="do-body">{info.body}</div>}

        {(info.form_fields || []).length > 0 && (
          <div className="space-y-3" data-testid="do-fields">
            {info.form_fields.map((f) => (
              <div key={f.key}>
                {f.type === "checkbox" ? (
                  <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" data-testid={`do-f-${f.key}`} checked={!!vals[f.key]} onChange={(e) => setV(f.key, e.target.checked)} className="w-4 h-4 mt-0.5 accent-indigo-500" /> {f.label}{f.required ? " *" : ""}</label>
                ) : f.type === "signature" ? (
                  <div><label className="text-xs font-semibold text-slate-600">{f.label}</label><SignaturePad onChange={(v) => setV(f.key, v)} /></div>
                ) : (
                  <div><label className="text-xs font-semibold text-slate-600">{f.label}{f.required ? " *" : ""}</label><Input data-testid={`do-f-${f.key}`} type={f.type === "date" ? "date" : "text"} value={vals[f.key] || ""} onChange={(e) => setV(f.key, e.target.value)} className="h-11 mt-1" /></div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="rounded-2xl border border-slate-200 p-4 space-y-3">
          <div className="flex gap-2">
            {[["draw", "Draw", PenLine], ["typed", "Type name", FileText]].map(([m, l, Icon]) => (
              <button key={m} data-testid={`do-mode-${m}`} onClick={() => { setMode(m); setSig(null); }} className={`flex-1 py-2 rounded-xl text-sm font-semibold inline-flex items-center justify-center gap-1.5 border ${mode === m ? "bg-indigo-500 text-white border-indigo-500" : "border-slate-200 text-slate-600"}`}><Icon size={15} /> {l}</button>
            ))}
          </div>
          {mode === "draw" && <SignaturePad onChange={setSig} />}
          {mode === "typed" && <p className="text-xs text-slate-500">Your typed full name below will act as your signature.</p>}
        </div>

        <NameInputs value={name} onChange={setName} testid="do-name" />
        <label className="flex items-start gap-2 text-sm text-slate-700"><input type="checkbox" data-testid="do-ack" checked={ack} onChange={(e) => setAck(e.target.checked)} className="w-4 h-4 mt-0.5 accent-indigo-500" /> I have read and {info.kind === "contract" ? "agree to this contract" : "completed this induction"}, and this is my electronic signature.</label>
        <Button data-testid="do-submit" onClick={submit} disabled={busy} className="w-full h-12 bg-indigo-500 hover:bg-indigo-400 text-white rounded-xl font-bold">{busy ? "Submitting…" : (info.kind === "contract" ? "Sign & submit" : "Complete & submit")}</Button>
        <p className="text-[11px] text-slate-400 text-center">Your name, the time, and your IP address are recorded for the audit trail.</p>
      </div>
    </Shell>
  );
}
